import { isPriceChangedError } from 'shared/ErrorTypeCheck';
import { centsToAmount } from 'shared/money';
import { CreateLeadBody } from 'store/widget/widget.types';
import { CREDIT_CARD_ID_NONE } from './constants';

export type PaySequenceOutcome = 'paid' | 'cancelled' | 'card-required';

export type LeadPayload = Omit<CreateLeadBody, 'creditCardId' | 'price'>;

export interface PaySequenceInput {
  payload: LeadPayload;
  priceCents: number;
  selectedCardId: string;
  setupNewCard: () => Promise<string>;
  pay: (body: CreateLeadBody) => Promise<unknown>;
  confirmPriceChanged: (postedPriceCents: number, serverPriceCents: number) => Promise<boolean>;
}

export interface ProceedPaymentGate {
  quotePriceCents: number | undefined;
  isQuoteFetching: boolean;
  isQuoteError: boolean;
  isSubmissionReady: boolean;
  selectedCardId: string;
  isNewCardIncomplete: boolean;
}

export interface PriceChangePromptState {
  postedPriceCents: number;
  serverPriceCents: number;
}

export interface PriceChangePromptDeps {
  invalidateQuote: () => void;
  showPrompt: (prompt: PriceChangePromptState | null) => void;
}

export interface PriceChangeActions {
  onConfirm: () => void;
  onBack: () => void;
}

export interface PriceChangePrompt {
  ask: (postedPriceCents: number, serverPriceCents: number) => Promise<boolean>;
  actions: PriceChangeActions;
}

export interface CardSetupCache {
  setUpOnce: (setupNewCard: () => Promise<string>) => Promise<string>;
  forget: () => void;
}

export interface PayOutcomeHandlers {
  onPaid: () => void;
  onCardRequired: () => void;
}

function toPayBody(payload: LeadPayload, priceCents: number, creditCardId: string | undefined): CreateLeadBody {
  return {
    ...payload,
    price: centsToAmount(priceCents),
    ...(creditCardId && priceCents > 0 ? { creditCardId } : {}),
  };
}

export async function runPaySequence({
  payload,
  priceCents,
  selectedCardId,
  setupNewCard,
  pay,
  confirmPriceChanged,
}: PaySequenceInput): Promise<PaySequenceOutcome> {
  let creditCardId: string | undefined;

  if (priceCents > 0) {
    creditCardId = selectedCardId === CREDIT_CARD_ID_NONE ? await setupNewCard() : selectedCardId;
  }

  const attempt = async (postedPriceCents: number): Promise<PaySequenceOutcome> => {
    try {
      await pay(toPayBody(payload, postedPriceCents, creditCardId));
      return 'paid';
    } catch (error) {
      if (!isPriceChangedError(error)) {
        throw error;
      }

      const { serverPrice } = error.data;
      const isConfirmed = await confirmPriceChanged(postedPriceCents, serverPrice);

      if (!isConfirmed) {
        return 'cancelled';
      }

      if (serverPrice > 0 && !creditCardId) {
        return 'card-required';
      }

      return attempt(serverPrice);
    }
  };

  return attempt(priceCents);
}

export function isProceedPaymentDisabled({
  quotePriceCents,
  isQuoteFetching,
  isQuoteError,
  isSubmissionReady,
  selectedCardId,
  isNewCardIncomplete,
}: ProceedPaymentGate): boolean {
  if (quotePriceCents === undefined || isQuoteFetching || isQuoteError || !isSubmissionReady) {
    return true;
  }

  return quotePriceCents > 0 && selectedCardId === CREDIT_CARD_ID_NONE && isNewCardIncomplete;
}

export function createPriceChangePrompt({ invalidateQuote, showPrompt }: PriceChangePromptDeps): PriceChangePrompt {
  let resolvePending: ((isConfirmed: boolean) => void) | null = null;

  const settle = (isConfirmed: boolean) => {
    const resolve = resolvePending;
    resolvePending = null;
    showPrompt(null);
    resolve?.(isConfirmed);
  };

  return {
    ask: (postedPriceCents, serverPriceCents) => {
      invalidateQuote();

      return new Promise<boolean>((resolve) => {
        resolvePending = resolve;
        showPrompt({ postedPriceCents, serverPriceCents });
      });
    },
    actions: {
      onConfirm: () => settle(true),
      onBack: () => settle(false),
    },
  };
}

export function createCardSetupCache(): CardSetupCache {
  let setUpCardId: string | null = null;

  return {
    setUpOnce: async (setupNewCard) => {
      if (setUpCardId === null) {
        setUpCardId = await setupNewCard();
      }

      return setUpCardId;
    },
    forget: () => {
      setUpCardId = null;
    },
  };
}

export function handlePayOutcome(outcome: PaySequenceOutcome, { onPaid, onCardRequired }: PayOutcomeHandlers): void {
  if (outcome === 'paid') {
    onPaid();
    return;
  }

  if (outcome === 'card-required') {
    onCardRequired();
  }
}
